// SEO Analytics and monitoring utilities

export interface SEOMetrics {
  pageViews: number;
  uniqueVisitors: number;
  bounceRate: number;
  avgSessionDuration: number;
  organicTraffic: number;
  keywordRankings: KeywordRanking[];
  coreWebVitals: CoreWebVitals;
}

export interface KeywordRanking {
  keyword: string;
  position: number;
  searchVolume: number;
  difficulty: number;
  url: string;
}

export interface CoreWebVitals {
  lcp: number; // Largest Contentful Paint
  fid: number; // First Input Delay
  cls: number; // Cumulative Layout Shift
  fcp: number; // First Contentful Paint
  ttfb: number; // Time to First Byte
}

export class SEOAnalytics {
  private static instance: SEOAnalytics;
  
  static getInstance(): SEOAnalytics {
    if (!SEOAnalytics.instance) {
      SEOAnalytics.instance = new SEOAnalytics();
    }
    return SEOAnalytics.instance;
  }

  // Initialize Google Analytics 4
  initializeGA4(measurementId: string): void {
    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', measurementId, {
      // Enhanced ecommerce for wallpaper downloads
      custom_map: {
        custom_parameter_1: 'wallpaper_category',
        custom_parameter_2: 'wallpaper_resolution',
        custom_parameter_3: 'device_type'
      }
    });
  }

  // Track wallpaper views
  trackWallpaperView(wallpaper: any): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'view_item', {
        item_id: wallpaper.id,
        item_name: wallpaper.title,
        item_category: wallpaper.category,
        item_brand: 'WallpaperHub',
        custom_parameter_1: wallpaper.category,
        custom_parameter_2: `${wallpaper.width}x${wallpaper.height}`,
        custom_parameter_3: this.detectDeviceType()
      });
    }
  }

  // Track wallpaper downloads
  trackWallpaperDownload(wallpaper: any, resolution: string): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'purchase', {
        transaction_id: `download_${wallpaper.id}_${Date.now()}`,
        value: 0, // Free download
        currency: 'USD',
        items: [{
          item_id: wallpaper.id,
          item_name: wallpaper.title,
          item_category: wallpaper.category,
          item_brand: 'WallpaperHub',
          quantity: 1,
          price: 0
        }],
        custom_parameter_1: wallpaper.category,
        custom_parameter_2: resolution,
        custom_parameter_3: this.detectDeviceType()
      });
    }
  }

  // Track search queries
  trackSearch(query: string, resultCount: number): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'search', {
        search_term: query,
        custom_parameter_1: resultCount > 0 ? 'results_found' : 'no_results',
        custom_parameter_2: resultCount.toString(),
        custom_parameter_3: this.detectDeviceType()
      });
    }
  }

  // Track category browsing
  trackCategoryView(category: string, wallpaperCount: number): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'view_item_list', {
        item_list_id: category,
        item_list_name: `${category} Wallpapers`,
        custom_parameter_1: category,
        custom_parameter_2: wallpaperCount.toString(),
        custom_parameter_3: this.detectDeviceType()
      });
    }
  }

  // Track user engagement
  trackEngagement(action: string, category: string, label?: string, value?: number): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
        custom_parameter_3: this.detectDeviceType()
      });
    }
  }

  // Detect device type for analytics
  private detectDeviceType(): string {
    const userAgent = navigator.userAgent;
    
    if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
      return 'tablet';
    }
    
    if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
      return 'mobile';
    }
    
    return 'desktop';
  }

  // Initialize Google Search Console integration
  initializeSearchConsole(): void {
    // Add Google Search Console verification meta tag
    const meta = document.createElement('meta');
    meta.name = 'google-site-verification';
    meta.content = process.env.VUE_APP_GOOGLE_SITE_VERIFICATION || '';
    document.head.appendChild(meta);
  }

  // Track Core Web Vitals for SEO
  trackCoreWebVitals(): void {
    // LCP - Largest Contentful Paint
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      this.reportWebVital('LCP', lastEntry.startTime);
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // FID - First Input Delay
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        const fidEntry = entry as any;
        const fid = fidEntry.processingStart - fidEntry.startTime;
        this.reportWebVital('FID', fid);
      });
    }).observe({ entryTypes: ['first-input'] });

    // CLS - Cumulative Layout Shift
    let clsValue = 0;
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        const clsEntry = entry as any;
        if (!clsEntry.hadRecentInput) {
          clsValue += clsEntry.value;
        }
      });
      
      this.reportWebVital('CLS', clsValue);
    }).observe({ entryTypes: ['layout-shift'] });

    // FCP - First Contentful Paint
    new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        this.reportWebVital('FCP', entry.startTime);
      });
    }).observe({ entryTypes: ['paint'] });
  }

  private reportWebVital(name: string, value: number): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', name, {
        event_category: 'Web Vitals',
        value: Math.round(value),
        non_interaction: true,
        custom_parameter_3: this.detectDeviceType()
      });
    }
  }

  // Generate SEO report
  async generateSEOReport(): Promise<SEOMetrics> {
    // This would typically fetch data from Google Analytics API
    // For now, return mock data structure
    return {
      pageViews: 0,
      uniqueVisitors: 0,
      bounceRate: 0,
      avgSessionDuration: 0,
      organicTraffic: 0,
      keywordRankings: [],
      coreWebVitals: {
        lcp: 0,
        fid: 0,
        cls: 0,
        fcp: 0,
        ttfb: 0
      }
    };
  }

  // Track social sharing
  trackSocialShare(platform: string, wallpaperId: string): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'share', {
        method: platform,
        content_type: 'wallpaper',
        item_id: wallpaperId,
        custom_parameter_3: this.detectDeviceType()
      });
    }
  }

  // Track user preferences
  trackUserPreference(preference: string, value: string): void {
    if (typeof (window as any).gtag !== 'undefined') {
      (window as any).gtag('event', 'user_preference', {
        event_category: 'User Behavior',
        event_label: preference,
        value: value,
        custom_parameter_3: this.detectDeviceType()
      });
    }
  }
}

// Extend window interface for gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Export singleton instance
export const seoAnalytics = SEOAnalytics.getInstance();